<template>
  <v-row no-gutters>
    <v-col cols="12">
      <span>Vendor Name</span>
    </v-col>
    <v-col cols="12">
      <v-select
        id="ddlVendorId"
        v-model="activeStudentManagementVendorSid"
        :items="clientStudentManagementVendorsListView"
        item-title="name"
        item-value="sid"
        variant="solo"
        density="compact"
        placeholder="Select Vendor Name"
        clearable
        :disabled="isStudentManagementUnchangeable"
        clear-icon="mdi-close"
        color="primary"
        @update:model-value="changeStudentManagementVendorSid"
      >
        <template #item="data">
          <v-list-item
            v-bind="data.props"
            :id="`client-individual-modal--student-management-vendor--vendor-name--option-${String(
              data.item.name
            ).replaceAll(' ', '')}`"
          >
            {{ data.item.name }}
          </v-list-item>
        </template>
      </v-select>
    </v-col>
  </v-row>
  <v-row v-if="showServiceEndedCheckbox" no-gutters>
    <v-col cols="12">
      <v-checkbox-btn
        id="cbxClientStudentManagementServiceEnded"
        v-model="serviceEnded"
        class="client-student-management-checkbox"
        hide-details
        color="primary"
        label="End Services"
        @update:model-value="changeServiceEnded"
      ></v-checkbox-btn>
    </v-col>
  </v-row>

  <client-student-management-config
    ref="refStudentManagementConfig"
    :module-name="activeVendorModuleName"
    :vendor-sid="activeStudentManagementVendorSid"
    :service-ended="serviceEnded"
    :student-management-config="studentManagementConfig"
    :student-management-files="studentManagementFiles"
    :student-management-file-extensions="studentManagementFileExtensions"
    :student-management-file-mime-type-allowed="studentManagementFileMimeTypeAllowed"
    :student-management-error-hash="studentManagementErrorHash"
    :student-management-config-valid="studentManagementConfigValid"
    @update:student-management-config="onStudentManagementConfigChanged"
    @update:student-management-files="onStudentManagementFilesChanged"
    @update:student-management-error-hash="onStudentManagementErrorHashChanged"
    @update:student-management-config-valid="onStudentManagementConfigValidChanged"
    @update:valid="onConfigValidChanged"
    @changed="emitStudentManagementChanged"
  />

  <v-alert
    v-if="showConfigUnsavedWarning"
    color="#ff7f32"
    icon-size="x-small"
    variant="outlined"
    icon="mdi-alert"
    class="mb-2 mt-3"
  >
    <b>Notice:</b> You have unsaved Student Management changes. Save Student Management before
    retrieving groups, otherwise Retrieve Groups from Vendor will use the last saved configuration.
  </v-alert>

  <client-student-management-grouping
    v-if="showGroupSection"
    :client-sid="clientSid"
    :module-name="activeVendorModuleName"
    :school-types="schoolTypes"
    :service-ended="serviceEnded"
    :group-list="groupList"
    :original-group-list="originalGroupList"
    :groups-loading="groupsLoading"
    :student-management-group-sync-diffs="studentManagementGroupSyncDiffs"
    :is-read-from-cache="isReadFromCache"
    :hide-selected="hideSelected"
    :search-term="searchTerm"
    :student-group-pagination-info="studentGroupPaginationInfo"
    :student-selection-in-risk-assessment-enabled="studentSelectionInRiskAssessmentEnabled"
    :retrieve-error-displayed="groupingRetrieveErrorDisplayed"
    :retrieve-error-message="groupingRetrieveErrorMessage"
    :retrieve-error-is-error="groupingRetrieveErrorIsError"
    @update:group-list="onGroupListChanged"
    @update:search-term="onSearchTermChanged"
    @update:student-selection-in-risk-assessment-enabled="onStudentSelectionInRiskAssessmentChanged"
    @update:retrieve-error-displayed="groupingRetrieveErrorDisplayed = $event"
    @clear-search="clearGroupSearch"
    @retrieve-groups="onRetrieveGroups"
    @changed="emitStudentManagementChanged"
  />

  <v-row>
    <v-col cols="12" class="d-flex justify-end align-center pr-1">
      <cs-button
        v-if="editMode === 'update'"
        id="btnSaveStudentManagement"
        primary
        label="Save Student Management"
        :disabled="saveButtonDisabled"
        :loading="saveActionInProgress"
        @click="openSaveConfirmation()"
      ></cs-button>
    </v-col>
  </v-row>
  <cs-form-dialog
    id="confirm-save-form-dialog"
    v-model="confirmSaveDialogDisplayed"
    heading="Client Settings Editor"
    :primary-action="{
      label: 'Continue',
      eventName: 'primary-click'
    }"
    @primary-click="onSave()"
  >
    <template #cs-form-dialog-content>
      <div>
        <group-mapping-summary
          :summary="groupMappingChangeSummary"
          :student-group-sync-diffs="studentManagementGroupSyncDiffs"
        />
        <span
          >You are about to save the student management settings. Are you sure you want to
          proceed?</span
        >
      </div>
    </template>
  </cs-form-dialog>
  <cs-form-dialog
    id="dlg-client-student-management-save-notice"
    v-model="noticeDialogDisplayed"
    body-id="dlg-client-student-management-save-notice-body"
    heading="Student Management"
    :primary-action="{
      label: 'OK',
      eventName: 'primary-click'
    }"
    @primary-click="onNoticeDialogOk"
  >
    <template #cs-form-dialog-content>
      <div v-safe-html="noticeDialogMessage"></div>
      <v-spacer></v-spacer>
      <div v-safe-html="noticeDialogWarningMessage" class="warning-message"></div>
    </template>
  </cs-form-dialog>
</template>

<script>
import { CSBase } from '@complispace/cs-design-system';
import { mapGetters } from 'vuex';
import ClientStudentManagementConfig from './ClientStudentManagementConfig';
import ClientStudentManagementGrouping from './ClientStudentManagementGrouping';
import * as types from '@/store/mutationTypes';
import buildStudentManagementConfigPayload from '../lib/buildStudentManagementConfigPayload';
import buildLegacyStudentManagement from '../lib/buildLegacyStudentManagement';
import buildLegacyGroupManagement from '../lib/buildLegacyGroupManagement';
import buildStudentGroupRows from '../lib/buildStudentGroupRows';
import buildStudentGroupingPayload from '../lib/buildStudentGroupingPayload';
import studentManagement from '../lib/studentManagement';
import GroupMappingSummary from './GroupMappingSummary';
import {
  DEFAULT_SUBSET_ATTRIBUTE_KEYS,
  VENDOR_SELECTION_ONLY_MODULES
} from '../lib/const/studentManagementConstants';
import cloneDeep from 'lodash/cloneDeep';

export default {
  name: 'ClientStudentManagement',

  components: {
    ClientStudentManagementConfig,
    ClientStudentManagementGrouping,
    GroupMappingSummary
  },

  extends: CSBase,

  props: {
    clientSid: {
      type: String,
      required: true,
      default: ''
    },
    schoolTypes: {
      type: Array,
      default: () => []
    }
  },

  emits: [
    'changeStudentManagementVendorSid',
    'studentManagementChanged',
    'studentManagementSaved',
    'update:serviceEnded',
    'update:valid'
  ],

  data() {
    return {
      editMode: 'create',
      isStudentManagementUnchangeable: false,
      activeStudentManagementVendorSid: null,
      showServiceEndedCheckbox: false,
      serviceEnded: false,
      configValid: true,
      studentManagementConfigValid: true,
      studentManagementConfig: {},
      studentManagementErrorHash: {},
      studentManagementFiles: [{}],
      studentManagementFileExtensions: '.csv',
      studentManagementFileMimeTypeAllowed: ['text/csv'],
      studentManagementFileChanged: false,
      isStudentManagementConfigChanged: false,
      complispacePreviewGrouping: null,
      studentSelectionInRiskAssessmentEnabled: false,
      groupList: [],
      originalGroupList: [],
      existingGroupList: [],
      schoolNameById: {},
      groupsLoading: false,
      studentManagementGroupSyncDiffs: [],
      isReadFromCache: false,
      hideSelected: true,
      searchTerm: '',
      studentGroupPaginationInfo: {
        itemsPerPage: 30,
        page: 1,
        sortBy: []
      },
      clientStudentGroupSubsetAttributeKeys: DEFAULT_SUBSET_ATTRIBUTE_KEYS,
      groupingRetrieveErrorDisplayed: false,
      groupingRetrieveErrorMessage: '',
      groupingRetrieveErrorIsError: false,
      saveActionInProgress: false,
      noticeDialogDisplayed: false,
      noticeDialogMessage: '',
      noticeDialogWarningMessage: '',
      confirmSaveDialogDisplayed: false,
      groupMappingChangeSummary: null
    };
  },

  computed: {
    ...mapGetters({
      clientStudentManagementView: 'clients/clientStudentManagementView',
      clientStudentManagementVendorsListView: 'clients/clientStudentManagementVendorsListView'
    }),
    activeVendorModuleName() {
      if (!this.activeStudentManagementVendorSid) {
        return '';
      }
      const vendors = this.clientStudentManagementVendorsListView || [];
      const vendor = vendors.find((item) => item.sid === this.activeStudentManagementVendorSid);

      return vendor ? vendor.moduleName : '';
    },

    activeVendorName() {
      if (!this.activeStudentManagementVendorSid) {
        return '';
      }
      const vendors = this.clientStudentManagementVendorsListView || [];
      const vendor = vendors.find((item) => item.sid === this.activeStudentManagementVendorSid);
      return vendor ? vendor.name : '';
    },

    isFileVendor() {
      return this.activeVendorModuleName === 'complispace';
    },
    isVendorSelectionOnlyVendor() {
      return VENDOR_SELECTION_ONLY_MODULES.has(this.activeVendorModuleName);
    },
    showGroupSection() {
      return (
        !!this.activeStudentManagementVendorSid &&
        !this.isFileVendor &&
        this.activeVendorModuleName !== 'sentral'
      );
    },

    showConfigUnsavedWarning() {
      return this.isStudentManagementConfigChanged && this.showGroupSection;
    },

    saveButtonDisabled() {
      return !this.configValid || this.saveActionInProgress;
    }
  },

  watch: {
    clientStudentManagementView() {
      this.reloadData();
    },
    activeStudentManagementVendorSid(newSid, oldSid) {
      if (newSid === oldSid) {
        return;
      }
      this.resetConfigState();
      this.resetGroupingState();
      this.isStudentManagementUnchangeable = false;
    }
  },

  async mounted() {
    await this.loadStudentVendorsList();
    if (this.clientSid) {
      this.editMode = 'update';
      await this.initPage(this.clientSid);
    }
  },

  methods: {
    async loadStudentVendorsList() {
      try {
        this.setLoading(true);
        await this.$store.dispatch('clients/fetchStudentManagementVendorList');
        this.clearLoading();
      } catch (e) {
        this.clearLoading();
      }
    },

    async initPage(clientSid) {
      if (clientSid !== '') {
        this.resetGroupingState();
        this.resetConfigState();
        await this.fetchStudentManagementConfig(clientSid);
        await this.loadGroupManagement();
        this.reloadData();
      }
    },

    async fetchStudentManagementConfig(clientSid) {
      if (!clientSid) {
        return;
      }
      try {
        this.setLoading(true);
        await this.$store.dispatch('clients/fetchClientStudentManagementConfig', {
          clientSid
        });
        this.clearLoading();
      } catch (e) {
        this.clearLoading();
      }
    },

    resetConfigState() {
      this.studentManagementConfigValid = true;
      this.studentManagementConfig = {};
      this.studentManagementErrorHash = {};
      this.studentManagementFiles = [{}];
      this.studentManagementFileChanged = false;
      this.complispacePreviewGrouping = null;
      this.isStudentManagementConfigChanged = false;
    },

    resetGroupingState() {
      this.studentSelectionInRiskAssessmentEnabled = false;
      this.isReadFromCache = false;
      this.searchTerm = '';
      this.groupList = [];
      this.originalGroupList = [];
      this.schoolNameById = {};
      this.studentManagementGroupSyncDiffs = [];
      this.groupsLoading = false;
      this.groupingRetrieveErrorDisplayed = false;
      this.groupingRetrieveErrorMessage = '';
      this.groupingRetrieveErrorIsError = false;
      this.studentGroupPaginationInfo = {
        itemsPerPage: 30,
        page: 1,
        sortBy: []
      };
    },

    setLoadedGroups(data) {
      this.groupsLoading = false;
      if (!data) {
        this.groupList = [];
        this.originalGroupList = [];
        this.schoolNameById = {};
        this.existingGroupList = [];
        this.isStudentManagementUnchangeable = false;
        return false;
      }

      this.groupList = buildStudentGroupRows(data, this.clientStudentGroupSubsetAttributeKeys);
      this.originalGroupList = cloneDeep(this.groupList);
      this.existingGroupList = cloneDeep(this.groupList);
      this.schoolNameById = ((data && data.schoolGroups) || []).reduce((acc, school) => {
        acc[`${school.id}`] = school.name;
        return acc;
      }, {});

      const hasGroups = this.groupList.length > 0;
      this.isStudentManagementUnchangeable = hasGroups;
    },

    mergeRemoteGroups(remoteGroupList) {
      this.groupsLoading = false;
      this.isReadFromCache = true;

      const existingGroupList = cloneDeep(this.groupList);

      this.studentManagementGroupSyncDiffs = studentManagement.getGroupSyncDiffs(
        this.existingGroupList,
        remoteGroupList
      );

      this.groupList = remoteGroupList.map((remoteGroup) => {
        const existingGroup = existingGroupList.find((s) => `${s.id}` === `${remoteGroup.id}`);

        if (existingGroup && existingGroup.group !== remoteGroup.name) {
          existingGroup.group = remoteGroup.name;
        }

        const resolvedGroup = existingGroup
          ? JSON.parse(JSON.stringify(existingGroup))
          : { id: remoteGroup.id, group: remoteGroup.name };

        this.clientStudentGroupSubsetAttributeKeys.forEach((k) => {
          if (typeof remoteGroup[k] !== 'undefined') {
            resolvedGroup[k] = remoteGroup[k];
          }
        });

        return resolvedGroup;
      });

      this.originalGroupList = cloneDeep(this.groupList);
      this.isStudentManagementUnchangeable = this.groupList.length > 0;
    },

    async openSaveConfirmation() {
      this.groupMappingChangeSummary = await this.previewGroupMappingChanges();
      this.confirmSaveDialogDisplayed = true;
    },

    async previewGroupMappingChanges() {
      if (!this.clientSid || !this.showGroupSection || !this.shouldSaveGrouping()) {
        return null;
      }
      const payload = buildStudentGroupingPayload(
        this.groupList,
        this.schoolNameById,
        this.clientStudentGroupSubsetAttributeKeys
      );

      try {
        const response = await this.$store.dispatch(
          'clients/patchClientStudentManagementGrouping',
          {
            clientSid: this.clientSid,
            payload
          }
        );
        return response?.error ? null : response;
      } catch (err) {
        console.error('error validating grouping change summary', err.message);
        return null;
      }
    },

    getGroupSyncDiffs() {
      return this.studentManagementGroupSyncDiffs;
    },

    shouldSaveGrouping() {
      return this.groupList.length !== 0 || this.groupList.length !== this.originalGroupList.length;
    },

    getComplispaceFile() {
      const studentManagementFile = this.studentManagementFiles.find((entry) => entry.file);
      return studentManagementFile?.file || null;
    },

    hasComplispaceFileChanged() {
      return this.isFileVendor && this.studentManagementFileChanged;
    },

    clearComplispaceFileChanged() {
      this.complispacePreviewGrouping = null;
      this.studentManagementFileChanged = false;
    },

    clearGroupSearch() {
      this.searchTerm = '';
    },

    changeStudentManagementVendorSid() {
      this.$emit('changeStudentManagementVendorSid', this.activeStudentManagementVendorSid);
      this.emitStudentManagementChanged();
    },

    reloadData() {
      const { studentVendorClientSid, isVendorServiceEnded, config } = cloneDeep(
        this.clientStudentManagementView
      );
      this.activeStudentManagementVendorSid = studentVendorClientSid;
      this.showServiceEndedCheckbox = !!this.activeStudentManagementVendorSid;
      this.serviceEnded = isVendorServiceEnded;
      this.studentManagementConfig = config || {};
      this.isStudentManagementConfigChanged = false;

      this.studentSelectionInRiskAssessmentEnabled =
        config?.studentSelectionInRiskAssessmentEnabled ?? false;

      this.isStudentManagementUnchangeable = this.groupList.length > 0;

      this.$emit('update:serviceEnded', this.serviceEnded);
      this.emitStudentManagementChanged();
    },

    changeServiceEnded() {
      this.$emit('update:serviceEnded', this.serviceEnded);
      this.emitStudentManagementChanged();
    },

    onStudentManagementConfigChanged(config, edited = true) {
      this.studentManagementConfig = config;
      if (edited) this.isStudentManagementConfigChanged = true;
      this.emitStudentManagementChanged();
    },

    onStudentManagementFilesChanged(files) {
      this.studentManagementFiles = files;
      this.studentManagementFileChanged = true;
      this.emitStudentManagementChanged();
    },

    onStudentManagementErrorHashChanged(errorHash) {
      this.studentManagementErrorHash = errorHash;
    },

    onStudentManagementConfigValidChanged(valid) {
      this.studentManagementConfigValid = valid;
    },

    onConfigValidChanged(valid) {
      this.configValid = valid;
      this.$emit('update:valid', valid);
    },

    onGroupListChanged(groupList) {
      this.groupList = groupList;
      this.emitStudentManagementChanged();
    },

    onSearchTermChanged(searchTerm) {
      this.searchTerm = searchTerm;
    },

    onStudentSelectionInRiskAssessmentChanged(enabled) {
      this.studentSelectionInRiskAssessmentEnabled = enabled;
      this.isReadFromCache = false;
      this.emitStudentManagementChanged();
    },

    async onRetrieveGroups(readFromCache) {
      if (!this.clientSid) {
        return;
      }

      this.groupsLoading = true;
      this.clearGroupSearch();

      try {
        const remoteGroupList = await this.$store.dispatch(
          'clients/fetchClientVendorStudentGroups',
          {
            clientSid: this.clientSid,
            queryParam: {
              'vendor-sid': this.$store.state.common.apiParam['vendor-sid'],
              'oauth-signature': this.$store.state.common.apiParam['oauth-signature'],
              readFromCache
            }
          }
        );
        this.mergeRemoteGroups(remoteGroupList);
        this.emitStudentManagementChanged();
      } catch (e) {
        this.groupsLoading = false;
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
        this.groupingRetrieveErrorDisplayed = true;
        this.groupingRetrieveErrorIsError = true;
        this.groupingRetrieveErrorMessage = 'Failed to retrieve client student groups.';
      }
    },

    async loadGroupManagement() {
      if (!this.clientSid || !this.activeStudentManagementVendorSid) {
        return;
      }

      this.groupsLoading = true;

      try {
        const data = await this.$store.dispatch('clients/fetchClientStudentManagementGrouping', {
          clientSid: this.clientSid,
          queryParam: {
            'vendor-sid': this.$store.state.common.apiParam['vendor-sid'],
            'oauth-signature': this.$store.state.common.apiParam['oauth-signature']
          }
        });

        this.setLoadedGroups(data);
      } catch (e) {
        this.setLoadedGroups(null);
        this.groupsLoading = false;
        this.isStudentManagementUnchangeable = false;
      }
    },

    async onSave() {
      this.confirmSaveDialogDisplayed = false;

      if (!this.clientSid) {
        return;
      }
      this.saveActionInProgress = true;
      try {
        this.$store.commit('app/setSilentAxiosError', true);
        const result = await this.save(this.clientSid);
        if (result && result.error) {
          this.handleSaveError(result.error);
          return;
        }
        this.alertSaveResult('success');
        await this.initPage(this.clientSid);
        this.$emit('studentManagementSaved');
      } catch (error) {
        this.handleSaveError(error);
      } finally {
        this.saveActionInProgress = false;
      }
    },

    alertSaveResult(status, errorMessage, warningMessage) {
      this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
      if (status === 'success') {
        this.noticeDialogMessage = 'Student Management Setting has been saved.';
        this.noticeDialogWarningMessage = warningMessage || '';
      } else {
        this.noticeDialogMessage = errorMessage || 'Failed to save Client information.';
        this.noticeDialogWarningMessage = warningMessage || '';
      }
      this.noticeDialogDisplayed = true;
    },

    handleSaveError(error) {
      let displayMessage = error.message || '';
      let warningMessage = '';
      try {
        const errorMessages = error.message?.split(':') || [];
        if (errorMessages.length > 3) {
          displayMessage = `Save Error: ${errorMessages[2]}`;
          warningMessage = `errorRefId: ${errorMessages[1]}`;
        }
        this.alertSaveResult(
          'error',
          `Failed to save Client information. <br/> ${displayMessage}`,
          warningMessage
        );
      } finally {
        this.$store.commit('app/setSilentAxiosError', true);
      }
    },

    onNoticeDialogOk() {
      this.noticeDialogDisplayed = false;
    },

    async saveStudentManagementGrouping(clientSid) {
      if (!clientSid || !this.activeStudentManagementVendorSid) {
        return { error: false };
      }

      const payload = buildStudentGroupingPayload(
        this.groupList,
        this.schoolNameById,
        this.clientStudentGroupSubsetAttributeKeys
      );

      const groupingResponse = await this.$store.dispatch(
        'clients/patchClientStudentManagementGrouping',
        {
          clientSid,
          payload: {
            ...payload,
            confirmedSave: true
          }
        }
      );

      if (groupingResponse && groupingResponse.error) {
        return groupingResponse;
      }

      return { error: false };
    },

    async save(clientSid) {
      const configResult = await this.saveStudentManagementConfig(clientSid);
      if (configResult && configResult.error) {
        return configResult;
      }

      if (this.isFileVendor) {
        if (this.hasComplispaceFileChanged()) {
          const groupingResult = await this.previewAndSaveComplispaceGrouping(clientSid);
          if (groupingResult && groupingResult.error) {
            return groupingResult;
          }
        }
        return { error: false };
      }

      if (this.showGroupSection && this.shouldSaveGrouping()) {
        const groupingResult = await this.saveStudentManagementGrouping(clientSid);
        if (groupingResult && groupingResult.error) {
          return groupingResult;
        }
      }

      return { error: false };
    },

    async previewAndSaveComplispaceGrouping(clientSid) {
      const file = this.getComplispaceFile();
      if (!file) {
        return { error: false };
      }

      let preview;
      if (this.complispacePreviewGrouping && this.hasComplispaceFileChanged()) {
        preview = this.complispacePreviewGrouping;
      } else {
        preview = await this.$store.dispatch(
          'clients/postClientStudentManagementGroupingCsvToJson',
          {
            clientSid,
            file
          }
        );
        if (preview && preview.error) {
          return preview;
        }
      }

      const groupingResponse = await this.$store.dispatch(
        'clients/patchClientStudentManagementGrouping',
        {
          clientSid,
          payload: {
            schoolGroups: preview.schoolGroups,
            cocurriculumGroups: preview.cocurriculumGroups,
            allGroups: preview.allGroups,
            confirmedSave: true
          }
        }
      );
      if (groupingResponse && groupingResponse.error) {
        return groupingResponse;
      }

      const schoolTypesResult = await this.overwriteExcursionManagementSchoolTypes(
        clientSid,
        this.deriveSchoolTypesFromGroups(preview.schoolGroups)
      );
      if (schoolTypesResult && schoolTypesResult.error) {
        return schoolTypesResult;
      }

      this.clearComplispaceFileChanged();

      return { error: false };
    },

    setComplispacePreviewGrouping(schoolGroups, cocurriculumGroups, allGroups) {
      this.groupList = buildStudentGroupRows({ schoolGroups, cocurriculumGroups, allGroups });
      this.emitStudentManagementChanged();
    },

    deriveSchoolTypesFromGroups(schoolGroups) {
      if (!Array.isArray(schoolGroups)) {
        return [];
      }
      return schoolGroups.map((school) => ({
        id: String(school.id),
        name: school.name
      }));
    },

    async overwriteExcursionManagementSchoolTypes(clientSid, schoolTypes) {
      if (!Array.isArray(schoolTypes) || schoolTypes.length === 0) {
        return { error: false };
      }

      await this.$store.dispatch('clients/fetchClientExcursionManagement', clientSid);
      const existingExcursionManagement =
        this.$store.getters['clients/clientExcursionManagementView'] || {};

      const patchResponse = await this.$store.dispatch('clients/patchClientExcursionManagement', {
        clientSid,
        clientExcursionManagement: {
          ...existingExcursionManagement,
          schoolTypes
        }
      });

      if (patchResponse && patchResponse.error) {
        return { error: patchResponse.error };
      }
      return { error: false };
    },

    async saveStudentManagementConfig(clientSid) {
      if (!clientSid || !this.activeStudentManagementVendorSid) {
        return { error: false };
      }

      const payload = buildStudentManagementConfigPayload(
        this.activeStudentManagementVendorSid,
        this.activeVendorModuleName,
        this.activeVendorName,
        this.serviceEnded,
        this.isVendorSelectionOnlyVendor,
        this.isFileVendor,
        this.studentManagementConfig,
        this.studentSelectionInRiskAssessmentEnabled
      );

      if (payload) {
        const configResponse = await this.$store.dispatch(
          'clients/patchClientStudentManagementConfig',
          {
            clientSid,
            payload
          }
        );
        if (configResponse && configResponse.error) {
          return configResponse;
        }
      }

      return { error: false };
    },

    async testConnectionBeforeSave() {
      return this.$refs.refStudentManagementConfig?.testConnectionBeforeSave() ?? true;
    },

    collectStudentManagement() {
      if (!this.activeStudentManagementVendorSid) return null;
      const legacyGroupManagement = buildLegacyGroupManagement(
        this.groupList,
        this.clientStudentGroupSubsetAttributeKeys
      );
      const studentManagement = buildLegacyStudentManagement(
        this.studentManagementConfig,
        legacyGroupManagement,
        this.showGroupSection,
        this.studentSelectionInRiskAssessmentEnabled,
        this.activeVendorModuleName === 'wonde'
      );

      return {
        activeStudentManagementVendorSid: this.activeStudentManagementVendorSid,
        studentVendorServiceEnded: this.serviceEnded,
        studentManagementFileChanged: this.hasComplispaceFileChanged(),
        studentManagement
      };
    },

    emitStudentManagementChanged() {
      this.$emit('studentManagementChanged', this.collectStudentManagement());
    }
  }
};
</script>
<style scoped>
.client-student-management-checkbox {
  margin-top: 0 !important;
  font-size: 16px;
  margin-bottom: 16px;
}
</style>
